import React, { useMemo, useState } from 'react';
import { Checkbox, CheckboxGroup, Input, Message, Placeholder, SelectPicker } from 'rsuite';
import { Button, ConfirmModal, Modal, useLastPresent } from '@nsi-advisory-services/components';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import { showToast } from '../utils/toast';
import { formatBytes, triggerBlobDownload } from '../utils/expenseAttachments';
import {
  useAssignOnboarding,
  useCreateOnboardingStep,
  useDeleteOnboardingStep,
  useOnboardingAssignments,
  useOnboardingProfileGroups,
  useOnboardingResponses,
  useOnboardingSteps,
  useOnboardingUploadContent,
  useReorderOnboardingSteps,
  useResetOnboarding,
  useUnassignOnboarding,
  useUpdateOnboardingStep,
} from '../hooks/useQueries';
import './OnboardingAdminPage.css';

// The step kinds the journey page knows how to render. Adding one here is half
// the work of adding a kind; the other half is STEP_KINDS in the server's
// onboarding route and the journey page's renderer.
const KIND_OPTIONS = [
  {
    value: 'acknowledge',
    label: 'Read and confirm',
    hint: 'A title and some supporting text. The employee confirms they have read it.',
  },
  {
    value: 'link',
    label: 'Visit a link',
    hint: 'The same, plus a link the employee has to open before they can confirm.',
  },
  {
    value: 'checklist',
    label: 'Tick a list of boxes',
    hint: 'Any number of checkboxes, each with its own text and an optional link. Every box has to be ticked before the step will confirm.',
  },
  {
    value: 'event',
    label: 'Schedule an event',
    hint: 'The employee enters the date they have scheduled something for. Nothing else to set up — the title and supporting text say what they are booking.',
  },
  {
    value: 'input',
    label: 'Answer a question',
    hint: 'The employee writes an answer in a text box and saves it. The title and supporting text ask the question; the answers are readable under People onboarding.',
  },
  {
    value: 'signature',
    label: 'Digital signature',
    hint: 'An optional link to the document being signed, the statement they are agreeing to, and a line where they type their full name as a signature. Signatures are readable under People onboarding.',
  },
  {
    value: 'upload',
    label: 'Upload a document',
    hint: 'An optional link to a document to fill in or sign, and a place to upload the completed copy. Uploads go into the Onboarding folder of the employee\'s document library, and are listed under People onboarding.',
  },
  {
    value: 'profile',
    label: 'Update info in Employee Connect',
    hint: 'The employee fills in their own profile. The step lists what is in and what is missing, sends them to their profile page, and will not confirm until it is all there.',
  },
];

const KIND_LABELS = KIND_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const STATUS_LABELS = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
};

const emptyStep = () => ({
  kind: 'acknowledge',
  title: '',
  body: '',
  link_url: '',
  link_label: '',
  profile_groups: [],
  checklist_items: [],
  person_source: null,
  person_employee_id: null,
  is_active: true,
});

const toFormValue = (step) => ({
  kind: step?.kind || 'acknowledge',
  title: step?.title || '',
  body: step?.body || '',
  link_url: step?.link_url || '',
  link_label: step?.link_label || '',
  profile_groups: Array.isArray(step?.profile_groups) ? step.profile_groups : [],
  // Ids come back with the boxes and go back out again: they key which boxes an
  // employee has ticked, so an edit must not invent new ones.
  checklist_items: Array.isArray(step?.checklist_items)
    ? step.checklist_items.map((item) => ({
      id: item.id,
      label: item.label || '',
      link_url: item.link_url || '',
      link_label: item.link_label || '',
    }))
    : [],
  person_source: step?.person_source || null,
  person_employee_id: step?.person_employee_id || null,
  is_active: step?.is_active !== false,
});

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const employeeName = (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email || 'Unknown';

/** The create/edit form. Each kind's own fields only exist for that kind. */
function StepFormFields({ value, onChange, error, profileGroups, peopleOptions }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const kindHint = KIND_OPTIONS.find((option) => option.value === value.kind)?.hint;
  const isSignature = value.kind === 'signature';
  const isUpload = value.kind === 'upload';
  // Signature and upload steps both point at a document, and for both it is
  // optional; a link step's link is the whole step.
  const linkIsDocument = isSignature || isUpload;

  const setItem = (index, patch) => set({
    checklist_items: value.checklist_items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
  });

  // No id on a new box: the server mints one when it is first saved, and every
  // later save sends the same id back so the ticks against it survive an edit.
  const addItem = () => set({
    checklist_items: [...value.checklist_items, { label: '', link_url: '', link_label: '' }],
  });

  const removeItem = (index) => set({
    checklist_items: value.checklist_items.filter((_item, i) => i !== index),
  });

  return (
    <div className="bdc-modal__fields">
      {error ? (
        <Message type="error" showIcon>
          {String(error?.message || error)}
        </Message>
      ) : null}

      <div>
        <div className="bdc-modal__label">Step type</div>
        <SelectPicker
          data={KIND_OPTIONS.map(({ value: optionValue, label }) => ({ value: optionValue, label }))}
          value={value.kind}
          onChange={(next) => set({ kind: next || 'acknowledge' })}
          cleanable={false}
          searchable={false}
          block
        />
        {kindHint ? <div className="bdc-modal__label" style={{ marginTop: 6 }}>{kindHint}</div> : null}
      </div>

      <div>
        <div className="bdc-modal__label">Title</div>
        <Input
          value={value.title}
          onChange={(next) => set({ title: next })}
          placeholder="Sign the employee handbook"
        />
      </div>

      <div>
        <div className="bdc-modal__label">
          {isSignature ? 'Statement to sign' : 'Supporting text'}
        </div>
        <Input
          as="textarea"
          rows={6}
          value={value.body}
          onChange={(next) => set({ body: next })}
          placeholder={isSignature
            ? 'By typing my name below, I confirm I have read and agree to the employee handbook.'
            : 'What the employee needs to know to finish this step.'}
        />
        {isSignature ? (
          <div className="bdc-modal__label" style={{ marginTop: 6 }}>
            Shown directly above the line they sign on.
          </div>
        ) : null}
      </div>

      {value.kind === 'link' || linkIsDocument ? (
        <>
          <div>
            <div className="bdc-modal__label">
              {linkIsDocument ? 'Document link (optional)' : 'Link address'}
            </div>
            <Input
              value={value.link_url}
              onChange={(next) => set({ link_url: next })}
              placeholder="https://…"
            />
            {linkIsDocument && value.link_url.trim() ? (
              <div className="bdc-modal__label" style={{ marginTop: 6 }}>
                {isSignature
                  ? 'They have to open it before they can sign.'
                  : 'They open this, fill it in or sign it, and upload the finished copy.'}
              </div>
            ) : null}
          </div>

          <div>
            <div className="bdc-modal__label">Link button text</div>
            <Input
              value={value.link_label}
              onChange={(next) => set({ link_label: next })}
              placeholder={linkIsDocument ? 'Open the document' : 'Open the handbook'}
              disabled={linkIsDocument && !value.link_url.trim()}
            />
          </div>
        </>
      ) : null}

      {value.kind === 'checklist' ? (
        <div>
          <div className="bdc-modal__label">Checkboxes</div>

          <div className="onba-boxeditor">
            {value.checklist_items.length === 0 ? (
              <div className="onba-boxeditor__empty">
                No boxes yet. Add the first one — the step cannot be finished without at least one.
              </div>
            ) : null}

            {value.checklist_items.map((item, index) => (
              <div className="onba-boxeditor__row" key={item.id || `new-${index}`}>
                <div className="onba-boxeditor__fields">
                  <Input
                    value={item.label}
                    onChange={(next) => setItem(index, { label: next })}
                    placeholder="What they are agreeing to"
                  />
                  <div className="onba-boxeditor__link">
                    <Input
                      value={item.link_url}
                      onChange={(next) => setItem(index, { link_url: next })}
                      placeholder="Optional link (https://…)"
                    />
                    <Input
                      value={item.link_label}
                      onChange={(next) => setItem(index, { link_label: next })}
                      placeholder="Link text"
                      disabled={!item.link_url.trim()}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="bdc-iconbtn"
                  onClick={() => removeItem(index)}
                  aria-label={`Remove checkbox ${index + 1}`}
                  title="Remove this checkbox"
                >
                  <i className="bi bi-trash" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>

          <button type="button" className="bdc-btn bdc-btn--sm" onClick={addItem}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Add checkbox
          </button>
        </div>
      ) : null}

      {value.kind === 'profile' ? (
        <div>
          <div className="bdc-modal__label">Information to collect</div>
          <CheckboxGroup
            value={value.profile_groups}
            onChange={(next) => set({ profile_groups: next || [] })}
          >
            {profileGroups.map((group) => (
              <Checkbox key={group.key} value={group.key}>
                {group.label}
                <span className="onba-fieldhint">
                  {group.fields
                    .map((field) => (field.optional ? `${field.label} (optional)` : field.label))
                    .join(', ')}
                </span>
              </Checkbox>
            ))}
          </CheckboxGroup>
          {value.profile_groups.length === 0 ? (
            <div className="bdc-modal__label">Pick at least one — a step that asks for nothing can never be finished.</div>
          ) : null}
        </div>
      ) : null}

      {value.kind === 'event' ? (
        <div>
          <div className="bdc-modal__label">Show a person on this step</div>

          {/* Two checkboxes rather than a picker with a "nobody" row, because
              that is how it reads: show their coach, or show someone else.
              Only one can be on — the card has room for one person. */}
          <Checkbox
            checked={value.person_source === 'coach'}
            onChange={(_v, checked) => set({
              person_source: checked ? 'coach' : null,
              person_employee_id: null,
            })}
          >
            Show their coach
            <span className="onba-fieldhint">
              Resolved per employee, so one step points every new starter at their own coach.
            </span>
          </Checkbox>

          <Checkbox
            checked={value.person_source === 'employee'}
            onChange={(_v, checked) => set({
              person_source: checked ? 'employee' : null,
              person_employee_id: checked ? value.person_employee_id : null,
            })}
          >
            Show someone else
            <span className="onba-fieldhint">The same person for everyone who takes this step.</span>
          </Checkbox>

          {value.person_source === 'employee' ? (
            <SelectPicker
              data={peopleOptions}
              value={value.person_employee_id}
              onChange={(next) => set({ person_employee_id: next })}
              searchable
              block
              placeholder="Select a person"
            />
          ) : null}
        </div>
      ) : null}

      <Checkbox checked={value.is_active} onChange={(_v, checked) => set({ is_active: checked })}>
        Active — include this step in the journey
      </Checkbox>
    </div>
  );
}

/**
 * One step in the journey list, draggable by its handle.
 *
 * The handle carries the drag listeners rather than the whole row: the row also
 * holds Edit and Delete, and a row that is entirely a drag target makes those
 * awkward to hit. Keyboard drag works from the handle too — it is a button, so
 * it takes focus, and dnd-kit's keyboard sensor moves the row from there.
 */
function SortableStepRow({ step, index, kindLabel, personLabel, profileGroupsLabel, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const classes = [
    'onba-step',
    step.is_active ? '' : 'onba-step--inactive',
    isDragging ? 'onba-step--dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={setNodeRef} style={style} className={classes}>
      <div className="onba-step__order">
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="onba-step__handle"
          aria-label={`Reorder ${step.title}`}
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <i className="bi bi-grip-vertical" aria-hidden="true" />
        </button>
        <span className="onba-step__position bdc-num">{index + 1}</span>
      </div>

      <div className="onba-step__main">
        <div className="onba-step__titlerow">
          <span className="onba-step__title">{step.title}</span>
          <span className="onba-chip">{kindLabel}</span>
          {step.is_active ? null : <span className="onba-chip onba-chip--muted">Inactive</span>}
        </div>

        {step.body ? <p className="onba-step__body">{step.body}</p> : null}

        {step.kind === 'link' && step.link_url ? (
          <span className="onba-step__link">
            <i className="bi bi-link-45deg" aria-hidden="true" />
            <span className="onba-step__linkurl">{step.link_url}</span>
            {step.link_label ? <span>· “{step.link_label}”</span> : null}
          </span>
        ) : null}

        {step.kind === 'upload' ? (
          <span className="onba-step__link">
            <i className="bi bi-cloud-upload" aria-hidden="true" />
            <span>
              {step.link_url ? 'Fill in ' : 'Asks for a document upload'}
            </span>
            {step.link_url ? (
              <>
                <span className="onba-step__linkurl">{step.link_url}</span>
                <span>, then upload the completed copy</span>
              </>
            ) : null}
          </span>
        ) : null}

        {step.kind === 'signature' ? (
          <span className="onba-step__link">
            <i className="bi bi-pen" aria-hidden="true" />
            {step.link_url ? (
              <>
                <span>Signs</span>
                <span className="onba-step__linkurl">{step.link_url}</span>
              </>
            ) : (
              <span>Asks for a typed signature</span>
            )}
          </span>
        ) : null}

        {step.kind === 'event' ? (
          <span className="onba-step__link">
            <i className="bi bi-calendar-event" aria-hidden="true" />
            <span>
              Asks for a scheduled date
              {step.person_source === 'coach' ? ', shows their coach' : ''}
              {step.person_source === 'employee' ? `, shows ${personLabel}` : ''}
            </span>
          </span>
        ) : null}

        {step.kind === 'checklist' && Array.isArray(step.checklist_items) ? (
          <span className="onba-step__link">
            <i className="bi bi-check2-square" aria-hidden="true" />
            <span>
              {step.checklist_items.length}
              {step.checklist_items.length === 1 ? ' checkbox' : ' checkboxes'}
              {step.checklist_items.some((item) => item.link_url) ? ', some with links' : ''}
            </span>
          </span>
        ) : null}

        {step.kind === 'profile' && Array.isArray(step.profile_groups) ? (
          <span className="onba-step__link">
            <i className="bi bi-person-vcard" aria-hidden="true" />
            <span>{profileGroupsLabel}</span>
          </span>
        ) : null}
      </div>

      <div className="onba-step__actions">
        <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => onEdit(step)}>
          <i className="bi bi-pencil" aria-hidden="true" />
          Edit
        </button>
        <button
          type="button"
          className="bdc-btn bdc-btn--sm bdc-btn--danger"
          onClick={() => onDelete(step)}
        >
          <i className="bi bi-trash" aria-hidden="true" />
          Delete
        </button>
      </div>
    </div>
  );
}

/**
 * Onboarding configuration.
 *
 * Two halves: the journey itself — the ordered steps every new starter walks —
 * and the people currently walking it. Both are here because they answer the
 * same question: what is onboarding, and who is in it.
 */
export default function OnboardingAdminPage() {
  const stepsQuery = useOnboardingSteps({ enabled: true });
  const assignmentsQuery = useOnboardingAssignments({ enabled: true });
  const profileGroupsQuery = useOnboardingProfileGroups({ enabled: true });

  const createStep = useCreateOnboardingStep();
  const updateStep = useUpdateOnboardingStep();
  const uploadContent = useOnboardingUploadContent();
  const deleteStep = useDeleteOnboardingStep();
  const reorderSteps = useReorderOnboardingSteps();
  const assign = useAssignOnboarding();
  const resetOnboarding = useResetOnboarding();
  const unassign = useUnassignOnboarding();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState(null);
  const [editorValue, setEditorValue] = useState(emptyStep());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEmployeeId, setAssignEmployeeId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [responsesTarget, setResponsesTarget] = useState(null);

  /*
    The order being worked on, or null while the list is simply the server's.
    Dragging edits this rather than saving: a drag is a rough movement and
    usually one of several, so the run is saved once, deliberately.
  */
  const [draftOrder, setDraftOrder] = useState(null);

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so a click on the handle is
    // still a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const profileGroups = useMemo(() => profileGroupsQuery.data?.groups || [], [profileGroupsQuery.data]);
  const profileGroupLabels = useMemo(() => (
    profileGroups.reduce((acc, group) => {
      acc[group.key] = group.label;
      return acc;
    }, {})
  ), [profileGroups]);

  const steps = useMemo(() => stepsQuery.data || [], [stepsQuery.data]);

  /*
    The list on screen. A draft names the steps it knew about, so anything added
    or removed since — by this page or another tab — is appended rather than
    dropped, and the draft can never lose a step.
  */
  const orderedSteps = useMemo(() => {
    if (!draftOrder) return steps;

    const byId = new Map(steps.map((step) => [step.id, step]));
    const drafted = draftOrder.map((id) => byId.get(id)).filter(Boolean);
    const draftedIds = new Set(drafted.map((step) => step.id));

    return [...drafted, ...steps.filter((step) => !draftedIds.has(step.id))];
  }, [steps, draftOrder]);

  // Dragging back to where it started is not a change to save.
  const isOrderDirty = useMemo(() => (
    orderedSteps.length === steps.length
    && orderedSteps.some((step, index) => steps[index]?.id !== step.id)
  ), [orderedSteps, steps]);

  const guard = useUnsavedChangesGuard(isOrderDirty);

  const activeSteps = useMemo(() => steps.filter((step) => step.is_active), [steps]);

  const employees = useMemo(() => assignmentsQuery.data?.employees || [], [assignmentsQuery.data]);
  const totalActiveSteps = assignmentsQuery.data?.total_steps ?? activeSteps.length;
  // Whether the journey asks anything at all. With no input or signature steps configured
  // the Responses button would open an empty dialog on every row, so it is not
  // offered at all until there is a question to answer.
  const hasInputSteps = (assignmentsQuery.data?.input_steps ?? 0) > 0;

  const responsesShown = useLastPresent(responsesTarget); // keeps the dialog steady while it fades out
  const responsesQuery = useOnboardingResponses(responsesShown?.employee_id, {
    enabled: Boolean(responsesTarget),
  });

  const assigned = useMemo(() => employees.filter((row) => row.status), [employees]);
  // Anyone on staff can be the person a step points at, whether or not they are
  // onboarding themselves — this is the same list the assign picker reads.
  const peopleLabels = useMemo(
    () => employees.reduce((acc, row) => {
      acc[row.employee_id] = employeeName(row);
      return acc;
    }, {}),
    [employees]
  );

  const peopleOptions = useMemo(
    () => employees.map((row) => ({
      value: row.employee_id,
      label: `${employeeName(row)}${row.email ? ` (${row.email})` : ''}`,
    })),
    [employees]
  );

  const assignable = useMemo(
    () => employees
      .filter((row) => !row.status)
      .map((row) => ({ value: row.employee_id, label: `${employeeName(row)}${row.email ? ` (${row.email})` : ''}` })),
    [employees]
  );

  const openCreate = () => {
    setEditorTarget(null);
    setEditorValue(emptyStep());
    setEditorOpen(true);
  };

  const openEdit = (step) => {
    setEditorTarget(step);
    setEditorValue(toFormValue(step));
    setEditorOpen(true);
  };

  const closeEditor = () => setEditorOpen(false);

  // Once the dialog has faded out, so the form doesn't empty while it is visible.
  const resetEditor = () => {
    setEditorTarget(null);
    setEditorValue(emptyStep());
  };

  const submitEditor = async () => {
    const hasLink = ['link', 'signature', 'upload'].includes(editorValue.kind);
    const payload = {
      kind: editorValue.kind,
      title: editorValue.title.trim(),
      body: editorValue.body.trim() || null,
      link_url: hasLink ? editorValue.link_url.trim() || null : null,
      link_label: hasLink ? editorValue.link_label.trim() || null : null,
      profile_groups: editorValue.kind === 'profile' ? editorValue.profile_groups : null,
      person_source: editorValue.kind === 'event' ? editorValue.person_source : null,
      person_employee_id: editorValue.kind === 'event' && editorValue.person_source === 'employee'
        ? editorValue.person_employee_id
        : null,
      checklist_items: editorValue.kind === 'checklist'
        ? editorValue.checklist_items
          .filter((item) => item.label.trim())
          .map((item) => ({
            id: item.id,
            label: item.label.trim(),
            link_url: item.link_url.trim() || null,
            link_label: item.link_label.trim() || null,
          }))
        : null,
      is_active: editorValue.is_active,
    };

    try {
      if (editorTarget?.id) {
        await updateStep.mutateAsync({ id: editorTarget.id, ...payload });
        showToast('success', 'Step updated.');
      } else {
        await createStep.mutateAsync(payload);
        showToast('success', 'Step added to the journey.');
      }
      // A created or deleted step makes the draft stale: it named a list that
      // no longer exists, so the order goes back to following the server.
      setDraftOrder(null);
      closeEditor();
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to save the step'));
    }
  };

  const downloadUpload = async (employeeId, upload) => {
    try {
      const result = await uploadContent.mutateAsync({ employeeId, uploadId: upload.id });
      triggerBlobDownload(result.blob, upload.file_name);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to download that file'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteStep.mutateAsync(deleteTarget.id);
      showToast('success', 'Step deleted.');
      setDraftOrder(null);
      setDeleteTarget(null);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to delete the step'));
    }
  };

  // Moving a step rewrites the whole order: the endpoint takes the full run
  // rather than a pair to swap, so positions can never drift out of step.
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = orderedSteps.map((step) => step.id);
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;

    setDraftOrder(arrayMove(ids, from, to));
  };

  const saveOrder = async () => {
    try {
      await reorderSteps.mutateAsync(orderedSteps.map((step) => step.id));
      setDraftOrder(null);
      showToast('success', 'Step order saved.');
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to save the order'));
    }
  };

  const submitAssign = async () => {
    if (!assignEmployeeId) return;
    try {
      await assign.mutateAsync({ employeeId: assignEmployeeId });
      showToast('success', 'Onboarding started.');
      setAssignOpen(false);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to start onboarding'));
    }
  };

  const confirmReset = async () => {
    if (!resetTarget?.employee_id) return;
    try {
      await resetOnboarding.mutateAsync(resetTarget.employee_id);
      showToast('success', `${employeeName(resetTarget)} is back at the first step.`);
      setResetTarget(null);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to reset'));
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget?.employee_id) return;
    try {
      await unassign.mutateAsync(removeTarget.employee_id);
      showToast('success', `${employeeName(removeTarget)} has been taken out of onboarding.`);
      setRemoveTarget(null);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to remove'));
    }
  };

  if (stepsQuery.isLoading) {
    return <Placeholder.Paragraph rows={8} active />;
  }

  const loadError = stepsQuery.error || assignmentsQuery.error;

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Onboarding' }]}
        icon="bi-signpost-2"
        title="Onboarding"
        /* Back navigates in code rather than through a link, so no click
           listener sees it. It asks the guard instead. */
        onBack={() => guard.requestLeave('/admin')}
        actions={(
          <button type="button" className="bdc-btn bdc-btn--primary" onClick={openCreate}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Add Step
          </button>
        )}
      />

      {loadError ? (
        <Message type="error" showIcon>
          {String(loadError?.message || loadError)}
        </Message>
      ) : null}

      <Section
        title="The journey"
        count={steps.length}
        aside={isOrderDirty ? (
          <div className="onba-orderactions">
            <span className="onba-orderactions__note">Order not saved</span>
            <button
              type="button"
              className="bdc-btn bdc-btn--sm"
              onClick={() => setDraftOrder(null)}
              disabled={reorderSteps.isPending}
            >
              Discard
            </button>
            <button
              type="button"
              className="bdc-btn bdc-btn--sm bdc-btn--primary"
              onClick={saveOrder}
              disabled={reorderSteps.isPending}
            >
              <i className="bi bi-check-lg" aria-hidden="true" />
              {reorderSteps.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        ) : null}
      >
        {steps.length === 0 ? (
          <div className="onba-steps">
            <div className="onba-empty">
              No steps yet. Add the first one and it becomes the start of the journey.
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            /* The list only ever reorders down a column, and a row dragged out
               of the card has nowhere to go. */
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedSteps.map((step) => step.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="onba-steps">
                {orderedSteps.map((step, index) => (
                  <SortableStepRow
                    key={step.id}
                    step={step}
                    index={index}
                    kindLabel={KIND_LABELS[step.kind] || step.kind}
                    personLabel={peopleLabels[step.person_employee_id] || 'a named person'}
                    profileGroupsLabel={(step.profile_groups || [])
                      .map((key) => profileGroupLabels[key] || key)
                      .join(', ')}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Section>

      <Section
        title="People onboarding"
        count={assigned.length}
        aside={(
          <button
            type="button"
            className="bdc-btn"
            onClick={() => {
              setAssignEmployeeId(null);
              setAssignOpen(true);
            }}
          >
            <i className="bi bi-person-plus" aria-hidden="true" />
            Start onboarding
          </button>
        )}
      >
        {assignmentsQuery.isLoading ? (
          <Placeholder.Paragraph rows={4} active />
        ) : assigned.length === 0 ? (
          <div className="onba-steps">
            <div className="onba-empty">
              Nobody is onboarding right now. Start it here, or from the hiring dashboard when a
              candidate is hired.
            </div>
          </div>
        ) : (
          <div className="nsi-table-wrap">
            <table className="nsi-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th className="nsi-table__right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assigned.map((row) => {
                  const done = row.completed_count || 0;
                  const percent = totalActiveSteps > 0 ? Math.round((done / totalActiveSteps) * 100) : 0;

                  return (
                    <tr key={row.employee_id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{employeeName(row)}</div>
                        <div className="nsi-table__muted">{row.email}</div>
                      </td>
                      <td>
                        <span className={`onba-chip${row.status === 'completed' ? ' onba-chip--good' : ''}`}>
                          {STATUS_LABELS[row.status] || row.status}
                        </span>
                      </td>
                      <td>
                        <div className="onba-progress">
                          <span className="bdc-num">{done} / {totalActiveSteps}</span>
                          <div className="onba-progress__track">
                            <div className="onba-progress__fill" style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="bdc-num">{formatDate(row.started_at)}</td>
                      <td className="bdc-num">{formatDate(row.completed_at)}</td>
                      <td className="nsi-table__right">
                        <div style={{ display: 'inline-flex', gap: 8 }}>
                          {hasInputSteps ? (
                            <button
                              type="button"
                              className="bdc-btn bdc-btn--sm"
                              onClick={() => setResponsesTarget(row)}
                              title="Read their answers, signatures and uploaded documents"
                            >
                              <i className="bi bi-chat-left-text" aria-hidden="true" />
                              Responses
                              {/* The count is the whole reason to press it or
                                  not, so it rides on the button rather than in
                                  a column of its own. */}
                              {row.response_count > 0 ? (
                                <span className="onba-chip onba-chip--count">{row.response_count}</span>
                              ) : null}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="bdc-btn bdc-btn--sm"
                            onClick={() => setResetTarget(row)}
                            title="Clear their progress and send them back to the first step"
                          >
                            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                            Reset
                          </button>
                          <button
                            type="button"
                            className="bdc-btn bdc-btn--sm bdc-btn--danger"
                            onClick={() => setRemoveTarget(row)}
                            title="Take them out of onboarding entirely"
                          >
                            <i className="bi bi-x-lg" aria-hidden="true" />
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Modal open={editorOpen} onClose={closeEditor} onExited={resetEditor} size="md">
        <Modal.Header>
          <Modal.Title>{editorTarget ? 'Edit step' : 'New step'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <StepFormFields
            value={editorValue}
            onChange={setEditorValue}
            error={editorTarget ? updateStep.error : createStep.error}
            profileGroups={profileGroups}
            peopleOptions={peopleOptions}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submitEditor}
            loading={createStep.isPending || updateStep.isPending}
            disabled={
              !editorValue.title.trim()
              || (editorValue.kind === 'link' && !editorValue.link_url.trim())
              || (editorValue.kind === 'signature' && !editorValue.link_url.trim() && !editorValue.body.trim())
              || (editorValue.kind === 'profile' && editorValue.profile_groups.length === 0)
              || (editorValue.kind === 'checklist'
                && editorValue.checklist_items.filter((item) => item.label.trim()).length === 0)
              || (editorValue.person_source === 'employee' && !editorValue.person_employee_id)
            }
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete step"
        body={`Delete “${deleteTarget?.title}”? Anyone part-way through onboarding loses their record of having completed it, and finishing the rest is all that is left of the journey.`}
        confirmText="Delete"
        danger
        loading={deleteStep.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)} onExited={() => setAssignEmployeeId(null)} size="xs">
        <Modal.Header>
          <Modal.Title>Start onboarding</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="bdc-modal__fields">
            {activeSteps.length === 0 ? (
              <Message type="warning" showIcon>
                There are no active steps yet, so there is nothing for them to do. Add a step first.
              </Message>
            ) : null}

            {assign.error ? (
              <Message type="error" showIcon>
                {String(assign.error?.message || assign.error)}
              </Message>
            ) : null}

            <div>
              <div className="bdc-modal__label">Employee</div>
              <SelectPicker
                data={assignable}
                value={assignEmployeeId}
                onChange={setAssignEmployeeId}
                searchable
                block
                placeholder="Select an employee"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submitAssign}
            loading={assign.isPending}
            disabled={!assignEmployeeId}
          >
            Start
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        open={!!guard.pendingTo}
        title="Leave without saving?"
        body="The order of the steps has been changed and not saved. Leaving now puts them back the way they were."
        confirmText="Leave without saving"
        cancelText="Stay here"
        danger
        onConfirm={guard.leave}
        onCancel={guard.stay}
      />

      <ConfirmModal
        open={!!resetTarget}
        title="Reset onboarding progress"
        body={`Send ${resetTarget ? employeeName(resetTarget) : 'this employee'} back to the first step? Every step they have completed is cleared, including any boxes they have ticked, and the journey page comes back for them. Files they uploaded stay in their document library.`}
        confirmText="Reset"
        danger
        loading={resetOnboarding.isPending}
        onConfirm={confirmReset}
        onCancel={() => setResetTarget(null)}
      />

      <ConfirmModal
        open={!!removeTarget}
        title="Remove from onboarding"
        body={`Take ${removeTarget ? employeeName(removeTarget) : 'this employee'} out of onboarding? The onboarding page disappears for them and their progress is discarded.`}
        confirmText="Remove"
        danger
        loading={unassign.isPending}
        onConfirm={confirmRemove}
        onCancel={() => setRemoveTarget(null)}
      />

      {/* What they wrote, read-only.

          Every question is listed, answered or not: "they have not done that
          one" is usually what an admin came to find out, and a list of only the
          answers cannot say it. */}
      <Modal open={!!responsesTarget} onClose={() => setResponsesTarget(null)} size="md" overflow>
        <Modal.Header>
          <Modal.Title>
            {responsesShown ? employeeName(responsesShown) : 'Responses'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* isPending, not isLoading: a query that has not resolved yet reports
              isLoading false, and falling through on undefined data is what
              made this dialog claim there were no questions. */}
          {responsesQuery.isPending ? (
            <Placeholder.Paragraph rows={5} active />
          ) : responsesQuery.isError ? (
            <Message type="error" showIcon header="Could not load the responses">
              {responsesQuery.error?.message || 'Try again in a moment.'}
            </Message>
          ) : (responsesQuery.data?.responses || []).length === 0 ? (
            <div className="onba-empty">
              There are no question, signature or upload steps on the journey, so there is nothing to read here.
            </div>
          ) : (
            <div className="onba-responses">
              {(responsesQuery.data?.responses || []).map((row) => (
                <section className="onba-response" key={row.step_id}>
                  <header className="onba-response__head">
                    <h4 className="onba-response__title">{row.title}</h4>
                    {!row.is_active ? (
                      <span className="onba-chip" title="This step has since been retired, but the answer is kept">
                        Retired step
                      </span>
                    ) : null}
                  </header>

                  {row.body ? <p className="onba-response__prompt">{row.body}</p> : null}

                  {row.kind === 'upload' ? (
                    (row.uploads || []).length > 0 ? (
                      <ul className="onba-response__files">
                        {row.uploads.map((upload) => (
                          <li className="onba-response__file" key={upload.id}>
                            <i className="bi bi-file-earmark-check" aria-hidden="true" />
                            <span className="onba-response__filename">{upload.file_name}</span>
                            <span className="onba-response__meta">
                              {[formatBytes(upload.size_bytes), formatDate(upload.uploaded_at)].filter(Boolean).join(' · ')}
                            </span>
                            <button
                              type="button"
                              className="bdc-btn bdc-btn--sm"
                              onClick={() => downloadUpload(responsesShown.employee_id, upload)}
                              disabled={uploadContent.isPending}
                            >
                              <i className="bi bi-download" aria-hidden="true" />
                              Download
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="onba-response__none">
                        {row.step_completed ? 'No upload recorded.' : 'Nothing uploaded yet.'}
                      </div>
                    )
                  ) : row.kind === 'signature' ? (
                    row.response_text ? (
                      <>
                        <div className="onba-response__signature">{row.response_text}</div>
                        <div className="onba-response__meta">Signed {formatDate(row.saved_at)}</div>
                      </>
                    ) : (
                      <div className="onba-response__none">
                        {row.step_completed ? 'No signature recorded.' : 'Not signed yet.'}
                      </div>
                    )
                  ) : row.response_text ? (
                    <>
                      {/* Pre-wrapped, not rendered: whatever paragraphing they
                          typed is part of the answer, and collapsing it would
                          reshape what somebody wrote. */}
                      <blockquote className="onba-response__text">{row.response_text}</blockquote>
                      <div className="onba-response__meta">Saved {formatDate(row.saved_at)}</div>
                    </>
                  ) : (
                    <div className="onba-response__none">
                      {row.step_completed ? 'No answer recorded.' : 'Not answered yet.'}
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setResponsesTarget(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
